var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { assertUserTypeOptional } from "healthex-common/checkusertype";
import { UpdateExternalResearcherOrganizationDetailsInput, } from "healthex-common/externalresearcherorganization.dtos";
import { InviteExternalResearcherUserInput, UpdateExternalResearcherUserInput, } from "healthex-common/externalresearcherorganizationuser.dtos";
import { UpdateHealthcareOrganizationDetailsInput } from "healthex-common/healthcareorganization.dtos";
import { InviteHealthcareOrganizationUserInput, UpdateHealthcareOrganizationUserInput, } from "healthex-common/healthcareorganizationuser.dtos";
import { UpdateServiceProviderOrganizationDetailsInput, } from "healthex-common/serviceproviderorganization.dtos";
import { InviteServiceProviderUserInput, UpdateServiceProviderUserInput } from "healthex-common/serviceprovideruser.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { pick, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED, ParameterizedRemoteData } from "../Infra/RemoteData";
export class OrganizationsManager {
    constructor(provisioningService, statsService, authManager, invalidationManager, studyService) {
        this.provisioningService = provisioningService;
        this.statsService = statsService;
        this.authManager = authManager;
        this.invalidationManager = invalidationManager;
        this.studyService = studyService;
        this.organizationPublicProfiles = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadOrganizationPublicProfiles());
        this.organizations = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadOrganizations());
        this.myOrganization = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadMyOrganization());
        this.myUser = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadMyUser());
        this.hasAtLeastOneStudy = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId], () => this.loadHasAtLeastOneStudy());
    }
    getOrganization(organizationId) {
        var _a;
        const myOrganization = this.myOrganization.value;
        if (myOrganization === NOT_LOADED)
            return NOT_LOADED;
        if (myOrganization === null)
            return null;
        if (organizationId === myOrganization.id) {
            return myOrganization;
        }
        else {
            const otherOrganizations = this.organizations.value;
            if (otherOrganizations === NOT_LOADED)
                return NOT_LOADED;
            if (otherOrganizations === null)
                return null;
            return (_a = otherOrganizations.find((candidate) => candidate.id === organizationId)) !== null && _a !== void 0 ? _a : null;
        }
    }
    async waitForReloadOrganizations() {
        await this.organizations.waitForReload();
        await this.myOrganization.waitForReload();
    }
    async waitForValueOrganizations() {
        await this.organizations.waitForValue();
        await this.myOrganization.waitForValue();
    }
    async createUserInvite(id) {
        const invite = {
            secret: `TBD: ${id}`,
            contractAddress: "TBD",
        };
        console.log("invite", invite);
        return invite;
    }
    async loadOrganizationPublicProfiles() {
        return await this.provisioningService.getOrganizationPublicProfiles();
    }
    async loadOrganizations() {
        if (!this.authManager.orgId)
            return null;
        if (!this.authManager.permissions.includes(UserPermissions.GLOBAL_PROVISIONING))
            return null;
        const organizations = await this.provisioningService.getOrganizations();
        return await Promise.all(organizations.map(async (organization) => {
            return {
                ...organization,
                users: (await this.provisioningService.getUsersByOrganization(organization.id)).results,
                features: null,
            };
        }));
    }
    async loadMyOrganization() {
        if (!this.authManager.orgId)
            return null;
        const organization = await this.provisioningService.getOrganizationById(this.authManager.orgId);
        if (!organization)
            return null;
        return {
            ...organization,
            users: this.authManager.permissions.includes(UserPermissions.ORG_ADMIN)
                ? (await this.provisioningService.getUsersByOrganization(this.authManager.orgId)).results
                : [],
        };
    }
    async loadMyUser() {
        if (this.authManager.orgId === null || this.authManager.id === null)
            return null;
        if (this.authManager.userType === UserType.PATIENT) {
            console.log("Skipping user lookup for patient - patients don't have user records");
            return null;
        }
        return await this.loadUserByOrganizationAndId(this.authManager.orgId, this.authManager.id);
    }
    async loadOrganizationPublicProfile(id) {
        return await this.provisioningService.getOrganizationPublicProfileById(id);
    }
    async loadOrganization(id) {
        return await this.provisioningService.getOrganizationById(id);
    }
    async loadHasAtLeastOneStudy() {
        if (this.authManager.orgId === null)
            return null;
        return await this.studyService.hasAtLeastOneStudy(this.authManager.orgId);
    }
    async isUserInvited(userId) {
        if (!this.authManager.id || !this.authManager.orgId)
            throw new Error("User is not logged in");
        const exists = await this.provisioningService.doesInviteExistForUser(userId, this.authManager.orgId);
        return exists;
    }
    async updateOrganizationDetails(organizationId, update) {
        let updateObject;
        if (update.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationDetailsInput(update);
        }
        else if (update.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherOrganizationDetailsInput(update);
        }
        else if (update.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderOrganizationDetailsInput(update);
        }
        else {
            willNeverHappen(update);
        }
        return await this.provisioningService.updateOrganizationDetails(organizationId, updateObject);
    }
    async createOrganization(name, type, initialUserInput) {
        const organizationId = await this.provisioningService.createOrganization({ name, type });
        console.log(`Created new organization: ${organizationId}`);
        await this.inviteUser(organizationId, initialUserInput);
        console.log(`Invited first user`);
        return organizationId;
    }
    async editOrganization(organizationId, input) {
        const existing = assertUserTypeOptional(await this.provisioningService.getOrganizationById(organizationId), input.type);
        if (!existing) {
            throw new Error("Editing an object that doesn't exist!");
        }
        if (input.name && input.name !== existing.name) {
            const organizationUpdate = {
                name: input.name,
                type: existing.type,
            };
            await this.provisioningService.updateOrganizationById(organizationId, organizationUpdate);
        }
        if (input.logo instanceof File) {
            const fileUploadInfo = await this.provisioningService.getOrganizationLogoUploadUrl(organizationId, input.logo);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.logo);
            input.logoUrl = fileUploadInfo.fileUrl;
        }
        let updateObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationDetailsInput(pick(input, "name", "address", "city", "state", "zip", "ssoEnabled", "allowDataAccessForPatientIdentification", "allowDataAccessForDataDelivery", "blockchainSigningType", "patientPortalLink", "patientPortalName", "vanitySubdomain", "logoUrl", "setupComplete"));
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherOrganizationDetailsInput(pick(input, "name", "ssoEnabled", "vanitySubdomain", "logoUrl", "setupComplete", "blockchainSigningType", "address", "city", "state", "zip"));
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderOrganizationDetailsInput(pick(input, "ssoEnabled", "vanitySubdomain", "logoUrl", "address", "city", "state", "zip"));
        }
        else {
            willNeverHappen(input);
        }
        await this.provisioningService.updateOrganizationDetails(organizationId, updateObject);
        await this.authManager.refreshTokenInfo();
    }
    async inviteUser(organizationId, input) {
        let inviteObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            inviteObject = new InviteHealthcareOrganizationUserInput(input);
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            inviteObject = new InviteExternalResearcherUserInput(input);
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            inviteObject = new InviteServiceProviderUserInput(input);
        }
        else {
            willNeverHappen(input);
        }
        const { id: userId } = await this.provisioningService.inviteUser(organizationId, inviteObject);
        return userId;
    }
    async loadUserByOrganizationAndId(organizationId, userId) {
        return await this.provisioningService.getUserByOrganizationAndId(organizationId, userId);
    }
    async loadUsersByOrganization(organizationId, start, end) {
        return await this.provisioningService.getUsersByOrganization(organizationId, undefined, start, end);
    }
    async editUser(organizationId, userId, input) {
        const existing = await this.provisioningService.getUserByOrganizationAndId(organizationId, userId);
        if (!existing) {
            throw new Error("Editing an object that doesn't exist!");
        }
        if (input.newAvatar) {
            const fileUploadInfo = await this.provisioningService.getUserAvatarUploadUrl(organizationId, userId, input.newAvatar);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.newAvatar);
            input.avatarUrl = fileUploadInfo.fileUrl;
        }
        const updateProperties = pick(input, "email", "firstName", "lastName", "title", "phone", "permissions", "avatarUrl", "onboardingComplete", "providerId");
        let updateObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationUserInput(updateProperties);
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherUserInput(updateProperties);
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderUserInput(updateProperties);
        }
        else {
            willNeverHappen(input);
        }
        const result = await this.provisioningService.updateUser(organizationId, userId, updateObject);
        return result;
    }
    async deleteUser(organizationId, userId) {
        await this.provisioningService.deleteUser(organizationId, userId);
        await this.organizations.waitForReload();
        await this.myOrganization.waitForReload();
    }
    async acceptInvite(inviteAddress, secret) {
        if (!this.authManager.id) {
            throw new Error("User not logged in");
        }
        console.warn("Accepting invite is disabled");
    }
    async sendInvite(userId) {
        const invite = await this.createUserInvite(userId);
        console.log("Sending invite to data originator", invite);
        if (!this.authManager.isLoggedIn) {
            throw new Error("User not logged in");
        }
        if (!this.authManager.orgId) {
            throw new Error("User's organization doesn't exist!");
        }
        console.warn("Sending invite is disabled");
    }
    async forcePasswordReset(organizationId, userId) {
        await this.provisioningService.forcePasswordUpdateForUser(organizationId, userId);
        await this.myOrganization.waitForReload();
    }
    get onlyUserIdWithPermission() {
        var _a;
        if (this.myOrganization.value === NOT_LOADED)
            return NOT_LOADED;
        if (!((_a = this.myOrganization.value) === null || _a === void 0 ? void 0 : _a.users))
            return null;
        const permissions = [UserPermissions.ORG_ADMIN, UserPermissions.RESEARCH_ADMIN, UserPermissions.RESEARCHER];
        const userIds = {};
        for (const permission of permissions) {
            userIds[permission] = undefined;
        }
        for (const user of this.myOrganization.value.users) {
            for (const permission of permissions) {
                if (userIds[permission] === null) {
                    continue;
                }
                if (user.permissions.includes(permission)) {
                    if (userIds[permission]) {
                        userIds[permission] = null;
                        continue;
                    }
                    userIds[permission] = user.id;
                }
            }
        }
        return userIds;
    }
    getCannotBeDeletedReason(user) {
        var _a, _b, _c, _d, _e;
        if (this.authManager.id === user.id) {
            return "SELF";
        }
        if (this.onlyUserIdWithPermission === NOT_LOADED) {
            return NOT_LOADED;
        }
        if (user.id === ((_a = this.onlyUserIdWithPermission) === null || _a === void 0 ? void 0 : _a.ORG_ADMIN) && user.id === ((_b = this.onlyUserIdWithPermission) === null || _b === void 0 ? void 0 : _b.RESEARCH_ADMIN)) {
            return "ONLY_ORG_AND_RESEARCH_ADMIN";
        }
        else if (user.id === ((_c = this.onlyUserIdWithPermission) === null || _c === void 0 ? void 0 : _c.ORG_ADMIN)) {
            return "ONLY_ORG_ADMIN";
        }
        else if (user.id === ((_d = this.onlyUserIdWithPermission) === null || _d === void 0 ? void 0 : _d.RESEARCHER) && this.hasAtLeastOneStudy.value === NOT_LOADED) {
            return NOT_LOADED;
        }
        else if (user.id === ((_e = this.onlyUserIdWithPermission) === null || _e === void 0 ? void 0 : _e.RESEARCHER) && this.hasAtLeastOneStudy.value === true) {
            return "ONLY_RESEARCHER_WITH_AT_LEAST_ONE_STUDY";
        }
        else {
            return null;
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationsManager.prototype, "myOrganization", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationsManager.prototype, "myUser", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationsManager.prototype, "hasAtLeastOneStudy", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], OrganizationsManager.prototype, "onlyUserIdWithPermission", null);
